<template>
  <main class="pdv-container">
    <header class="header px-1">
      <div class="d-flex column align-items-center justify-content-between">
        <div class="d-flex column align-items-center">
          <span class="brand-logo mr-1">
            <b-img
              id="logo"
              :src="style ? style.reducedHeaderImage : ''"
              alt="logo"
              fluid
              class="rounded"
            />
          </span>
          <h1 class="text-dark h2 mb-0">
            {{ getPageTitle }}
          </h1>
        </div>

        <CurrentCustomerInfo />

        <b-navbar-nav class="pr-1">
          <b-button
            variant="link"
            class="text-dark text-decoration-none px-1"
            title="Tela Cheia"
            @click="toggleFullScreen"
          >
            <b-icon icon="fullscreen" font-scale="1.3" />
          </b-button>
          <chat-tool class="chat-tool-nav-paybox" />
          <user-dropdown />
        </b-navbar-nav>
      </div>
    </header>

    <div class="content-pdv">
      <router-view />
    </div>

    <aside
      id="paybox_options"
      class="aside"
      :class="{ 'aside-show': !hideAside }"
      @click="hideAside = false"
      @mouseenter="onClearAsideTimer()"
      @mouseleave="onHideAside(true)"
    >
      <pay-box-aside
        :hide-aside="hideAside"
        @show-sidebar="onShowSidebar"
      />
    </aside>

    <modal-product-option ref="modalProductOption" />

    <pay-box-sidebar ref="sidebarHelper" />

    <modal-tef-messages />

    <dev-button
      class="position-fixed"
      style="opacity: 0.3; bottom: 100px; rotate: 270deg; left: -52px; z-index: 10"
    />
  </main>
</template>

<script>
import { BNavbarNav, BImg, BButton, BIcon } from 'bootstrap-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import PayBoxSidebar from '@/views/pages/pdv/pay-box/components/PayBoxSidebar.vue'
import PayBoxAside from '@/views/pages/pdv/pay-box/components/PayBoxAside.vue'
import UserDropdown from '@/layouts/pay-box/components/UserDropdown.vue'
import isElectron from 'is-electron'
import {
  app,
  loadingOverlay,
  payboxConfiguration,
  pdvBrowserNotAllowedConfiguration,
  scanBarcode,
  payboxCommands,
} from '@/mixins'

import ModalTefMessages from '@/views/pages/pdv/pay-box/components/modals/ModalTefMessages.vue'
import ChatTool from '@/layouts/components/ChatTool.vue'
import DevButton from '@/views/components/dev/DevButton.vue'
import ModalProductOption from '@/views/pages/pdv/components/modals/ModalProductOption.vue'
import CurrentCustomerInfo from './components/CurrentCustomerInfo.vue'

export default {
  components: {
    PayBoxSidebar,
    PayBoxAside,
    UserDropdown,
    ModalTefMessages,
    BNavbarNav,
    ChatTool,
    BImg,
    DevButton,
    CurrentCustomerInfo,
    ModalProductOption,
    BButton,
    BIcon,
  },

  mixins: [
    app,
    loadingOverlay,
    pdvBrowserNotAllowedConfiguration,
    payboxConfiguration,
    scanBarcode,
    payboxCommands,
  ],

  data() {
    return {
      hideAside: true,
      asideTimer: null,
    }
  },

  computed: {
    ...mapState('pages/pdv/payBox', ['choppGiftCardData']),
    ...mapGetters('pages/pdv', ['needCashUp']),
    ...mapGetters('pages/pdv/payBox', [
      'isOrder',
      'isChoppGiftCardConversion',
      'isBarConsumption',
      'isConsignedSalePayment',
      'isConsignedSaleFinish',
      'getSaleData',
    ]),

    getPageTitle() {
      const { pageTitle, isDynamicPageTitle } = this.$route.meta
      const { id, barTabConsumption } = this.getSaleData
      if (isDynamicPageTitle) {
        if (this.isConsignedSaleFinish) return `Venda #${id || ''} - Finalizar Consignado`
        if (this.isConsignedSalePayment) return `Venda #${id || ''} - Registrar Pagamento`
        if (this.isOrder) return `Venda #${id || ''} - Finalizar Pedido`
        if (this.isBarConsumption) return `Fechar Comanda #${barTabConsumption.barTabNumber || ''}`
        if (this.isChoppGiftCardConversion)
          return `Converter Cartão de Presente #${this.choppGiftCardData.number || ''}`
      }

      return pageTitle || 'PDV'
    },
  },

  watch: {
    async $route() {
      await this.pageLoadPdv(true)
      this.onScanAttachDefault()
    },
  },

  async beforeMount() {
    this.mxRedirectWhenMount = true
    if (isElectron()) await window.electronAPI.system.setFullScreen(true)
  },

  async mounted() {
    if (isElectron()) {
      window.electronAPI.tef.displayMessage(async result => {
        await this.stTefHandleMessage(result)
      })
      window.electronAPI.invoice.displayMessage(result => this.stInvoiceHandleMessage(result))
    }

    this.onScanAttachDefault()

    this.stSetModalProductOptionRef(this.$refs.modalProductOption)
  },

  beforeDestroy() {
    this.stSetModalProductOptionRef(null)
  },

  methods: {
    ...mapActions('pages/pdv', {
      stSetModalProductOptionRef: 'setModalProductOptionRef',
    }),
    ...mapActions('pages/pdv/payBoxTef', {
      stTefHandleMessage: 'handleMessage',
    }),
    ...mapActions('pages/pdv/payBox', {
      stInvoiceHandleMessage: 'handleInvoiceMessage',
    }),

    onClearAsideTimer() {
      clearTimeout(this.asideTimer)
    },

    onHideAside(isLeave) {
      this.onClearAsideTimer()
      this.asideTimer = setTimeout(() => {
        this.hideAside = isLeave
      }, 400)
    },

    async onShowSidebar(type) {
      this.$refs.sidebarHelper.onShowSidebar(type)
    },

    async toggleFullScreen() {
      if(!isElectron()) return

      const isFullScreen = await window.electronAPI.system.isFullScreen()
      await window.electronAPI.system.setFullScreen(!isFullScreen)
    }
  },
}
</script>

<style lang="scss" scoped>
$col1: 25px;
$col2: 320px;
$sumCol: calc(#{$col1} + #{$col2});

.navbar-nav {
  flex-direction: row;
}

.pdv-container {
  height: 100vh;
  display: grid;
  grid-template-columns: $col1 $col2 1fr;
  grid-template-rows: 50px 1fr;

  .aside {
    grid-area: 2 / 1 / 3 / 3;
    background: var(--dark);
    z-index: 10;
    transform: translateX(-$col2);
    transition: ease 0s;
    transition-duration: 0.5s;
    cursor: pointer;
  }

  .aside-show {
    transform: translateX(0);
    cursor: default;
    // transition-delay: 0.5s;
  }

  .header {
    grid-area: 1 / 1 / 2 / 5;
    background: var(--light);
    border-bottom: 1px solid var(--theme-color);
  }

  .content-pdv {
    grid-area: 2 / 2 / 3 / 5;
    overflow-y: auto;
  }
}

.brand-logo {
  box-sizing: border-box;
  width: 35px;
}

.chat-tool-nav-paybox {
  padding-top: 0.5rem;
}
</style>
